import * as React from "react"
import { ChevronUp, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

// Custom number input — stepper is position:absolute inside the wrapper, can never escape it
const NumberInputInner = React.forwardRef(
  ({ className, onChange, value, min, max, step = 1, disabled, ...props }, ref) => {
    const handleStep = (dir) => {
      if (disabled) return;
      const current = parseFloat(String(value)) || 0;
      const s = parseFloat(String(step)) || 1;
      let next = dir === 'up' ? current + s : current - s;
      if (min !== undefined) next = Math.max(Number(min), next);
      if (max !== undefined) next = Math.min(Number(max), next);
      const decimals = String(step).includes('.') ? String(step).split('.')[1].length : 0;
      next = parseFloat(next.toFixed(decimals));
      onChange?.({ target: { value: String(next) } });
    };

    return (
      <div
        style={{ boxSizing: 'border-box' }}
        className={cn(
          "relative w-full max-w-full min-h-[28px] overflow-hidden rounded-md border border-input bg-transparent shadow-sm focus-within:ring-1 focus-within:ring-ring",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        {/* Input — h-full fills wrapper, pr-7 reserves 28px for the 20px stepper + gap */}
        <input
          ref={ref}
          type="number"
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          style={{ boxSizing: 'border-box', width: '100%', lineHeight: 'normal' }}
          className="block h-full w-full max-w-full bg-transparent py-0 pl-2 pr-7 text-center align-middle focus:outline-none disabled:cursor-not-allowed"
          {...props}
        />
        {/* Stepper — absolutely anchored to right edge, never overflows */}
        <div
          className="absolute right-0 top-0 bottom-0 w-5 flex flex-col border-l border-input"
          style={{ position: 'absolute' }}
        >
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled}
            onMouseDown={(e) => { e.preventDefault(); handleStep('up'); }}
            className="flex-1 flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:pointer-events-none"
          >
            <ChevronUp className="w-2.5 h-2.5" />
          </button>
          <div className="h-px bg-input" />
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled}
            onMouseDown={(e) => { e.preventDefault(); handleStep('down'); }}
            className="flex-1 flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:pointer-events-none"
          >
            <ChevronDown className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
    );
  }
);
NumberInputInner.displayName = "NumberInputInner";

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
  if (type === 'number') {
    return <NumberInputInner ref={ref} className={className} {...props} />;
  }
  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
